import { useState } from 'react';
import { testimonials } from '@/mocks/testimonials';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9rffl4nlsngrm4lmbdg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormStatus('success');
      setFormError('');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    const formData = new FormData(form);

    fetch(FORM_SUBMIT_ADDR, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed: Record<string, unknown> | null = null;
        try { parsed = JSON.parse(text); } catch { parsed = null; }

        if (!res.ok) {
          throw new Error(parsed?.meta?.message as string || parsed?.message as string || text || '提交失败');
        }

        const code = parsed?.code;
        if (code === 'OK') {
          setFormStatus('success');
          form.reset();
        } else {
          throw new Error(parsed?.meta?.message as string || parsed?.message as string || '提交失败');
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : '提交失败，请稍后再试';
        setFormStatus('error');
        setFormError(msg);
      });
  };

  return (
    <main className="w-full px-4 md:px-6 pt-24 pb-16">
      <div className="max-w-7xl mx-auto">
        <section className="text-center mb-16">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            关于<span className="text-primary-500">开体育在线</span>
          </h1>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            开体育在线致力于打造中国领先的综合性体育赛事直播与资讯平台，
            以专业的内容、流畅的体验和全面的赛事覆盖，连接每一位热爱体育的用户。
          </p>
        </section>

        <section className="mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                我们的<span className="text-primary-500">故事</span>
              </h2>
              <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>
                  开体育在线创立于2023年，由一群热爱体育的互联网从业者共同发起。
                  我们深知，在信息爆炸的时代，体育爱好者需要的不仅仅是一个观看直播的窗口，
                  更是一个能够深度参与、获取专业资讯、分享热爱的互动社区。
                </p>
                <p>
                  经过三年多的发展，开体育在线已经成长为覆盖<strong>6大体育项目</strong>、
                  拥有<strong>超过500万注册用户</strong>的综合体育平台。
                  我们与国内外多家顶级体育赛事版权方合作，确保用户能够第一时间观看到高质量的赛事直播。
                </p>
                <p>
                  我们始终坚信：<strong>体育的力量在于连接</strong>。无论是顶级赛事的巅峰对决，
                  还是草根赛场的激情瞬间，开体育在线致力于让每一个热爱体育的人，
                  都能在这里找到属于自己的那份热血与感动。
                </p>
              </div>
            </div>
            <div className="aspect-[4/3] rounded-lg overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20sports%20media%20office%20with%20multiple%20screens%20showing%20live%20sports%2C%20team%20of%20young%20professionals%20working%2C%20warm%20lighting%2C%20minimalist%20design%2C%20creative%20workspace%2C%20sports%20broadcasting%20studio%20aesthetic%2C%20golden%20hour%20light&width=800&height=600&seq=about-story-img&orientation=landscape"
                alt="开体育在线工作环境"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="mb-16 py-12 md:py-16 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6 rounded-none">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
              平台<span className="text-primary-500">优势</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-live-line', title: '高清直播', desc: '4K超清画质赛事直播，低延迟流畅播放体验' },
                { icon: 'ri-bar-chart-2-line', title: '专业数据', desc: '实时赛事数据追踪，多维度竞技分析' },
                { icon: 'ri-newspaper-line', title: '深度资讯', desc: '专业编辑团队，深度赛事报道与分析' },
                { icon: 'ri-group-line', title: '互动社区', desc: '赛事弹幕互动，球迷社区交流讨论' },
              ].map((item, i) => (
                <div key={i} className="text-center p-6 rounded-lg bg-background-50 border border-background-200">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10">
            用户<span className="text-primary-500">口碑</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                    loading="lazy"
                  />
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed">{t.content}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-4">
            联系<span className="text-primary-500">我们</span>
          </h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            有任何问题或合作意向，欢迎通过以下方式与我们取得联系
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[
              { icon: 'ri-mail-line', label: '电子邮箱', value: 'contact@kaitiyuonline.com' },
              { icon: 'ri-phone-line', label: '客服电话', value: '400-888-6688' },
              { icon: 'ri-map-pin-line', label: '公司地址', value: '中国北京市朝阳区体育中心' },
            ].map((item, i) => (
              <div key={i} className="text-center p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <div className="text-xs text-foreground-500 mb-1">{item.label}</div>
                <div className="text-sm font-medium text-foreground-800">{item.value}</div>
              </div>
            ))}
          </div>

          <div className="p-6 md:p-8 rounded-lg bg-background-100 border border-background-200">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">发送消息</h3>
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="about-name">姓名</label>
                  <input
                    id="about-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="about-email">邮箱</label>
                  <input
                    id="about-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="about-subject">主题</label>
                <input
                  id="about-subject"
                  name="subject"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入消息主题"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5" htmlFor="about-message">消息内容</label>
                <textarea
                  id="about-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  placeholder="请输入您的消息（最多500字）"
                ></textarea>
              </div>

              <input
                type="text"
                name="phone_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'submitting' ? '发送中...' : '发送消息'}
              </button>

              {formStatus === 'success' && (
                <div className="p-3 rounded-lg bg-secondary-100 text-secondary-800 text-sm text-center">
                  消息已成功发送，我们会尽快与您联系！
                </div>
              )}
              {formStatus === 'error' && (
                <div className="p-3 rounded-lg bg-primary-100 text-primary-700 text-sm text-center">
                  {formError || '发送失败，请稍后再试。'}
                </div>
              )}
            </form>
          </div>
        </section>
      </div>
    </main>
  );
}